import { PlanTrailActionContext } from "./types";
import { StageConnector } from "../../../services/stage-connectors";
import { StageDestination, stageEndDayForMarker, stageEndForDay, stageStartDayForMarker, stageStartForDay } from "../../../services/stage-destinations";
import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../../../../utils/models/MarkerFeature";
import { generateID } from "../../../../../utils/helpers/IDGenerator";
import { AddMarkerCommand } from "../../undo/commands/AddMarkerCommand";
import { destinationHighlightChange, detachedStopHighlightId } from "../../../services/destination-highlights";
import { PlacePick, pickDestination, pickHighlight } from "../../../services/place-picks";

/**
 * Stage destinations — store actions.
 *
 * Attaching moves the day's END marker exactly onto the item's coordinates
 * (re-routing the last segment via the normal markerMoved path) and records
 * the destination. When the day was connected to the next one, the next
 * day's START marker moves too, so the days stay joined on the item.
 *
 * Detection/validity rules are pure and live in
 * services/stage-destinations.ts; drag-to-attach interaction lives in
 * markers.ts / stage-connectors.ts.
 */
export const stageDestinationActions = {
  /**
   * A stop pinned to a place also becomes one of that day's highlights, once
   * — or, already one, moves to that day (see destinationHighlightChange).
   * Called wherever a destination is set.
   */
  addHighlightForDestination(
    { state, commit, dispatch }: PlanTrailActionContext,
    payload: { destination: StageDestination; dayNumber?: number | null }
  ): void {
    const change = destinationHighlightChange(payload.destination, payload.dayNumber, (state as any).hikeHighlights || []);
    if (!change) return;
    if (change.kind === 'add') {
      commit('addHikeHighlight', change.highlight);
    } else {
      commit('updateHikeHighlight', { clientId: change.clientId, updates: { dayNumber: change.dayNumber } });
    }
    dispatch('refreshHighlights');
  },

  /**
   * A stop's place is no longer its stop — detached, dragged away, or the
   * stop pinned somewhere else — so the plain highlight made for the place
   * goes with it (see detachedStopHighlightId). Called after the
   * destination has been removed or replaced, so what's left is known.
   */
  async dropStopHighlight(
    { state, commit, dispatch }: PlanTrailActionContext,
    destination: StageDestination | null | undefined
  ): Promise<void> {
    if (!destination) return;
    const remaining = [...Object.values(state.stageDestinations ?? {}), state.trailStartDestination];
    const clientId = detachedStopHighlightId(destination, remaining, (state as any).hikeHighlights || []);
    if (!clientId) return;
    await dispatch('removeHighlightFromMap', clientId);
    commit('removeHikeHighlight', clientId);
    await dispatch('refreshHighlights');
  },

  /**
   * What a stop picked in the place search is pinned to (see pickDestination).
   * A Google Maps place first becomes a highlight of `dayNumber`, so there's
   * something on the plan to pin to; nothing is added to our own places.
   */
  addHighlightForPick(
    { commit, dispatch }: PlanTrailActionContext,
    payload: { pick: PlacePick; dayNumber: number }
  ): StageDestination | null {
    const clientId = `highlight_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    const highlight = pickHighlight(payload.pick, payload.dayNumber, clientId);
    if (highlight) {
      commit('addHikeHighlight', highlight);
      dispatch('refreshHighlights');
    }
    return pickDestination(payload.pick, highlight ? clientId : null);
  },

  /**
   * A route's Start or End picked from search (RouteEndpoints.vue): a place
   * to stay or an amenity (`destination`), or a plain address or point.
   *  - A place goes the way dropping the end on it does (see
   *    dropEndpointOnDestination), so it's recorded as the day's stop.
   *  - A plain point on the flag between two days moves that night there — a
   *    detour when it's off the line, since the user picked it outright.
   *  - Otherwise the end moves there and the route re-routes to it, taking a
   *    route joined to it within the day along.
   *  - A route without that end yet gets it, as "Add to route" would.
   * Then the map moves to show what changed: the pick can be anywhere.
   */
  async setRouteEndpoint(
    { state, getters, commit, dispatch }: PlanTrailActionContext,
    payload: { routeKey: string; which: 'start' | 'end'; coordinates: [number, number]; destination?: StageDestination | null }
  ): Promise<boolean> {
    const { routeKey, which, coordinates, destination } = payload;
    const route = state.routes[routeKey];
    if (!route) return false;
    const [lng, lat] = coordinates;

    const shown = async (routeKeys: string[]) => {
      await dispatch('showRoutes', routeKeys);
      return true;
    };

    if (route.markers.length === 0 || (which === 'end' && route.markers.length < 2)) {
      commit('setActiveRoute', routeKey);
      if (destination) await dispatch('addDestinationToRoute', { destination });
      else await dispatch('addMarkerAtCoordinates', { lng, lat });
      return shown([routeKey]);
    }

    const markerIndex = which === 'start' ? 0 : route.markers.length - 1;
    if (destination && await dispatch('dropEndpointOnDestination', { routeKey, markerIndex, destination })) return shown([routeKey]);

    const joinedHere = (joins: StageConnector[] = []) => joins.find(join =>
      which === 'end' ? join.prevRouteKey === routeKey : join.nextRouteKey === routeKey);
    const flag = joinedHere(getters?.stageConnectors);
    if (flag) {
      const moved = await dispatch('moveDayFlag', {
        prevRouteKey: flag.prevRouteKey, nextRouteKey: flag.nextRouteKey, targetCoord: coordinates, allowDetour: true,
      });
      if (moved === 'moved') {
        await dispatch('updateRouteAndMarkers');
        return shown([flag.prevRouteKey, flag.nextRouteKey]);
      }
    }

    // Read before anything moves: once this end goes, the two no longer meet.
    const join = joinedHere(getters?.routeJoins);
    const moveEnd = async (key: string, index: number) => {
      const marker = state.routes[key]?.markers[index];
      if (!marker) return;
      const oldPosition = [...marker.markerGeometry.coordinates];
      marker.markerGeometry.coordinates = [lng, lat];
      await dispatch('markerMoved', { marker, oldPosition, routeKey: key });
    };
    await moveEnd(routeKey, markerIndex);
    const otherKey = join ? (which === 'end' ? join.nextRouteKey : join.prevRouteKey) : null;
    if (otherKey) await moveEnd(otherKey, which === 'end' ? 0 : state.routes[otherKey].markers.length - 1);
    await dispatch('addConsolidatedMarkersToMap');
    return shown(otherKey ? [routeKey, otherKey] : [routeKey]);
  },

  /**
   * Carries a route on past its end (RouteEndpoints.vue's "+"): the end it
   * had stays as a control point, the route is drawn on to `coordinates`,
   * and a place picked is recorded as that end's stop. Then the map shows it.
   */
  async extendRouteEndpoint(
    { state, commit, dispatch }: PlanTrailActionContext,
    payload: { routeKey: string; coordinates: [number, number]; destination?: StageDestination | null }
  ): Promise<boolean> {
    const { routeKey, coordinates, destination } = payload;
    const route = state.routes[routeKey];
    if (!route) return false;
    commit('setActiveRoute', routeKey);
    await dispatch('addMarkerAtCoordinates', { lng: coordinates[0], lat: coordinates[1] });
    if (destination) {
      const markerIndex = state.routes[routeKey].markers.length - 1;
      await dispatch('dropEndpointOnDestination', { routeKey, markerIndex, destination });
    }
    await dispatch('showRoutes', [routeKey]);
    return true;
  },

  /**
   * "Add to route" on a POI (its detail sheet, or right-click): exactly what
   * dragging the active route's end onto it does — see dropEndpointOnDestination.
   * An empty route starts on the item instead; a route that's still only its
   * start point gets the item as its end; an offshoot leg (no day end) just
   * gets a control point there.
   */
  async addDestinationToRoute(
    { state, dispatch }: PlanTrailActionContext,
    payload: { destination: StageDestination }
  ): Promise<void> {
    const { destination } = payload;
    const stateAny = state as any;
    const lngLat = { lng: destination.coordinates[0], lat: destination.coordinates[1] };
    const routeKey = stateAny.activeRouteKey;
    const route = routeKey ? stateAny.routes[routeKey] : null;

    if (!route || route.markers.length === 0) {
      const dayNumber = route ? (route.day ?? 1) : null;
      await (dispatch as any)('addMarkerAtCoordinates', lngLat);
      if (dayNumber != null) await (dispatch as any)('attachTrailStart', { dayNumber, destination });
      return;
    }

    if (route.markers.length < 2) await (dispatch as any)('addMarkerAtCoordinates', lngLat);
    const handled = await (dispatch as any)('dropEndpointOnDestination', {
      routeKey, markerIndex: stateAny.routes[routeKey].markers.length - 1, destination,
    });
    if (!handled) await (dispatch as any)('addMarkerAtCoordinates', lngLat);
  },

  /**
   * A route endpoint dropped onto a POI/waypoint/highlight — what dragging a
   * day's end (or the trail's start) onto an item does, and what the item's
   * "Add to route" does too, so the two can't drift apart. A day's end that
   * the next day starts from is a flag: the line stays as it is and the night
   * detours out to the item (see attachStageDestination). Any other end, or a
   * start, moves exactly onto the item. Either way the item is recorded as
   * that day's destination, or as the trail start.
   * Returns false when the marker is neither a day's end nor a day's start.
   */
  async dropEndpointOnDestination(
    { state, dispatch }: PlanTrailActionContext,
    payload: { routeKey: string; markerIndex: number; destination: StageDestination }
  ): Promise<boolean> {
    const { routeKey, markerIndex, destination } = payload;
    const stateAny = state as any;
    const route = stateAny.routes[routeKey];
    if (!route) return false;

    const dayNumber = stageEndDayForMarker(stateAny.routes, routeKey, markerIndex);
    const startDayNumber = stageStartDayForMarker(stateAny.routes, routeKey, markerIndex);
    if (dayNumber == null && startDayNumber == null) return false;

    if (startDayNumber != null) {
      await (dispatch as any)('attachTrailStart', { dayNumber: startDayNumber, destination });
    } else if (dayNumber != null) {
      await (dispatch as any)('attachStageDestination', { dayNumber, destination });
    }
    return true;
  },

  async attachStageDestination(
    { state, getters, commit, dispatch }: PlanTrailActionContext,
    payload: { dayNumber: number; destination: StageDestination }
  ): Promise<boolean> {
    const { dayNumber, destination } = payload;
    const stageEnd = stageEndForDay(state.routes, dayNumber);
    if (!stageEnd) return false;
    // Pinned somewhere else before: that place's own highlight goes once the new stop is in.
    const previous: StageDestination | null = state.stageDestinations?.[dayNumber] ?? null;

    const route = state.routes[stageEnd.routeKey];
    if (!route) return false;

    const target: [number, number] = [destination.coordinates[0], destination.coordinates[1]];

    // The route doesn't have a real END yet — just its start point (with
    // only one marker, "the day's end" IS the start marker — there's no
    // second point to distinguish them). Attaching here must EXTEND the
    // route to end at this item, the same as "Add control point" would,
    // rather than moving the lone marker onto it — which would just
    // relocate the start, not create an end.
    if (route.markers.length < 2) {
      const win = window as any;
      if (route.isReference &&
          typeof win.canEditReference === 'function' && !win.canEditReference(route)) {
        if (typeof win.checkReferenceEditWarning === 'function') {
          win.checkReferenceEditWarning(route);
        }
        return false;
      }

      const startMarker = route.markers[0];
      if (!startMarker) return false;

      const newMarker = new MarkerFeature(
        new MarkerGeometry([target[0], target[1]]),
        new MarkerProperties(generateID(), state.isSnap, Date.now().toString(), 0)
      );

      const markerIndex = route.markers.length;
      route.markers.push(newMarker);

      const lineString = await dispatch('createPolylineGeoJson', { startMarker, endMarker: newMarker });
      route.lineStrings.push(lineString);
      route.updated_at = new Date();

      state.undoManager.push(new AddMarkerCommand({
        routeKey: stageEnd.routeKey,
        markerIndex,
        marker: newMarker,
        lineString,
      }));

      commit('setStageDestination', { dayNumber, destination: { ...destination, coordinates: target } });
      dispatch('addHighlightForDestination', { destination: { ...destination, coordinates: target }, dayNumber });
      await dispatch('dropStopHighlight', previous);
      await dispatch('updateRouteAndMarkers');
      return true;
    }

    const marker = route.markers[stageEnd.markerIndex];
    if (!marker) return false;

    // An end already exactly on the item (the "+" after a route's End row
    // adds the point AT the pick, then records it as the stop) just needs
    // the stop recorded: moving it would re-route the leg that's still
    // waiting for GraphHopper and leave the undo stack a move whose "before"
    // is the provisional straight line — undo would bring the dashed line
    // back instead of taking the point away.
    const current = marker.markerGeometry.coordinates;
    if (current[0] === target[0] && current[1] === target[1]) {
      commit('setStageDestination', { dayNumber, destination: { ...destination, coordinates: target } });
      dispatch('addHighlightForDestination', { destination: { ...destination, coordinates: target }, dayNumber });
      await dispatch('dropStopHighlight', previous);
      // Nothing moved, so nothing refreshed the ring around the item — draw it now.
      await dispatch('addConsolidatedMarkersToMap');
      await dispatch('updateStageConnectorsOnMap');
      await dispatch('updateTrailEndpointMarkersOnMap');
      return true;
    }

    // Read the connector BEFORE moving anything — once the end marker moves
    // away, the pair may exceed the connector threshold and stop reporting.
    const connector: StageConnector | undefined = (getters.stageConnectors as StageConnector[])
      .find(c => c.prevRouteKey === stageEnd.routeKey);

    // A day's end the next day starts from is a flag: the line the two days
    // walk stays as it is, and the night detours out to the item (moveDayFlag
    // records the destination).
    if (connector) {
      const moved = await dispatch('moveDayFlag', {
        prevRouteKey: connector.prevRouteKey,
        nextRouteKey: connector.nextRouteKey,
        targetCoord: target,
        destination,
      });
      if (moved === 'moved') {
        await dispatch('updateRouteAndMarkers');
        return true;
      }
    }

    // Move the marker first, then record the destination: markerMoved
    // refreshes stage decorations, which prune destinations whose end
    // marker isn't on the item.
    const oldPosition = [...marker.markerGeometry.coordinates];
    marker.markerGeometry.coordinates = [...target];
    commit('setStageDestination', { dayNumber, destination: { ...destination, coordinates: target } });
    dispatch('addHighlightForDestination', { destination: { ...destination, coordinates: target }, dayNumber });
    await dispatch('dropStopHighlight', previous);
    await dispatch('markerMoved', { marker, oldPosition, routeKey: stageEnd.routeKey });

    // Keep connected days joined — bring the next day's start along
    if (connector) {
      const nextRoute = state.routes[connector.nextRouteKey];
      const nextMarker = nextRoute?.markers[0];
      if (nextMarker) {
        const nextOldPosition = [...nextMarker.markerGeometry.coordinates];
        nextMarker.markerGeometry.coordinates = [...target];
        await dispatch('markerMoved', { marker: nextMarker, oldPosition: nextOldPosition, routeKey: connector.nextRouteKey });
      }
    }

    // Rebuild the consolidated markers so the attached end marker is
    // suppressed beneath its destination ring
    await dispatch('addConsolidatedMarkersToMap');

    return true;
  },

  /**
   * Remove a day's destination WITHOUT moving any marker — the badge goes
   * back to its normal form and the day loses its destination row.
   */
  async detachStageDestination({ state, commit, dispatch }: PlanTrailActionContext, dayNumber: number) {
    if (!(dayNumber in state.stageDestinations)) return;
    const removed = state.stageDestinations[dayNumber];
    commit('removeStageDestination', dayNumber);
    await dispatch('dropStopHighlight', removed);

    // Refresh the decorations that render differently while attached
    await dispatch('addConsolidatedMarkersToMap');
    await dispatch('updateStageConnectorsOnMap');
    await dispatch('updateTrailEndpointMarkersOnMap');
  },

  /**
   * Attach a day's stage-start (the first marker of that day's first
   * main-line route) to a map item — the "set as start point" action from
   * the POI/waypoint/highlight dialogs, called right after that first marker
   * is created (already sitting exactly on the item, so nothing needs to
   * move) or, potentially, on an existing start marker dragged onto an item
   * later (mirrors attachStageDestination's move-then-record behaviour).
   * `dayNumber` is whichever day is actually being edited (its own active
   * empty route, or the route a start marker was dragged from) — NOT
   * necessarily the plan's first day, since a later day can have its own
   * independent start when it isn't chained onto the previous day's end.
   * Renders as a ring around the item's icon, same as an attached stage end
   * — see updateTrailEndpointMarkersOnMap.
   */
  async attachTrailStart(
    { state, getters, commit, dispatch }: PlanTrailActionContext,
    payload: { dayNumber: number; destination: StageDestination }
  ): Promise<boolean> {
    const { dayNumber, destination } = payload;
    const start = stageStartForDay(state.routes, dayNumber);
    if (!start) return false;
    const previous: StageDestination | null = state.trailStartDestination ?? null;

    const route = state.routes[start.routeKey];
    const marker = route?.markers[start.markerIndex];
    if (!marker) return false;

    const target: [number, number] = [destination.coordinates[0], destination.coordinates[1]];
    const current = marker.markerGeometry.coordinates;
    const alreadyThere = current[0] === target[0] && current[1] === target[1];

    // A start the previous day ends at is the flag between them — it moves
    // like one, and the place becomes the previous day's destination.
    const connector: StageConnector | undefined = alreadyThere ? undefined : ((getters?.stageConnectors ?? []) as StageConnector[])
      .find(c => c.nextRouteKey === start.routeKey);
    if (connector) {
      const moved = await dispatch('moveDayFlag', {
        prevRouteKey: connector.prevRouteKey,
        nextRouteKey: connector.nextRouteKey,
        targetCoord: target,
        destination,
      });
      if (moved === 'moved') {
        await dispatch('updateRouteAndMarkers');
        return true;
      }
    }

    commit('setTrailStartDestination', { ...destination, coordinates: target });
    dispatch('addHighlightForDestination', { destination: { ...destination, coordinates: target }, dayNumber });
    await dispatch('dropStopHighlight', previous);

    if (!alreadyThere) {
      const oldPosition = [...marker.markerGeometry.coordinates];
      marker.markerGeometry.coordinates = [target[0], target[1], marker.markerGeometry.coordinates[2] ?? 0];
      await dispatch('markerMoved', { marker, oldPosition, routeKey: start.routeKey });
    }

    // The ring-around-the-icon itself is drawn by updateStageConnectorsOnMap
    // (buildHandleData), not updateTrailEndpointMarkersOnMap (which only
    // suppresses the plain pin) — markerMoved already triggers it when the
    // marker had to move, but the common case (a fresh marker already
    // created exactly on the destination — see the 3 "set as start point"
    // dialogs) never moves anything, so it must be called explicitly here
    // too or the ring wouldn't appear until some later action refreshed it.
    await dispatch('addConsolidatedMarkersToMap');
    await dispatch('updateStageConnectorsOnMap');
    await dispatch('updateTrailEndpointMarkersOnMap');
    return true;
  },

  /**
   * Remove the trail-start destination WITHOUT moving any marker — mirrors
   * detachStageDestination.
   */
  async detachTrailStart({ state, commit, dispatch }: PlanTrailActionContext) {
    if (!state.trailStartDestination) return;
    const removed = state.trailStartDestination;
    commit('removeTrailStartDestination');
    await dispatch('dropStopHighlight', removed);

    await dispatch('addConsolidatedMarkersToMap');
    await dispatch('updateStageConnectorsOnMap');
    await dispatch('updateTrailEndpointMarkersOnMap');
  },
};
